// Persists which request tabs are open, per workspace.
//
// The URL is the source of truth: `?tabs=req_a,req_b,req_c` keeps the open-
// tab list shareable + deep-linkable. localStorage acts as a fallback so a
// fresh visit (no tabs param) still resurrects the prior session. Every
// mutation writes both.

import { useCallback, useEffect, useMemo } from "react";
import { useSearchParams } from "react-router-dom";

const PARAM = "tabs";

function lsKey(workspaceId: string): string {
  return `rest.openTabs:${workspaceId}`;
}
function readLS(workspaceId: string): string[] {
  try {
    const v = localStorage.getItem(lsKey(workspaceId));
    if (!v) return [];
    const parsed = JSON.parse(v);
    return Array.isArray(parsed) ? parsed.filter((x) => typeof x === "string") : [];
  } catch { return []; }
}
function writeLS(workspaceId: string, tabs: string[]): void {
  try { localStorage.setItem(lsKey(workspaceId), JSON.stringify(tabs)); } catch { /* ignore */ }
}

function parseParam(v: string | null): string[] {
  if (!v) return [];
  return v.split(",").map((s) => s.trim()).filter(Boolean);
}

export function useOpenTabs(workspaceId: string, activeId: string | null) {
  const [sp, setSp] = useSearchParams();

  // Tabs come from the URL first; on the very first visit (no param yet),
  // hydrate from localStorage.
  const urlTabs = useMemo(() => parseParam(sp.get(PARAM)), [sp]);
  const tabs = useMemo<string[]>(() => {
    if (urlTabs.length > 0) return urlTabs;
    return readLS(workspaceId);
  }, [urlTabs, workspaceId]);

  // Shared updater: persists to both URL and localStorage, then returns. We
  // accept either a value or an updater so callers can read-modify-write.
  const setTabs = useCallback((next: string[] | ((prev: string[]) => string[])) => {
    const computed = typeof next === "function" ? next(tabs) : next;
    writeLS(workspaceId, computed);
    const params = new URLSearchParams(sp);
    if (computed.length === 0) params.delete(PARAM);
    else params.set(PARAM, computed.join(","));
    setSp(params, { replace: true });
  }, [tabs, workspaceId, sp, setSp]);

  // Auto-add activeId when navigation hits a new request.
  useEffect(() => {
    if (!activeId) return;
    if (tabs.includes(activeId)) return;
    setTabs([...tabs, activeId]);
  }, [activeId, tabs, setTabs]);

  const close = useCallback((id: string) => {
    setTabs((prev) => prev.filter((t) => t !== id));
  }, [setTabs]);

  const closeAll = useCallback(() => setTabs([]), [setTabs]);

  // Stand-alone helper for callers that don't have access to the hook
  // (e.g., the row-level delete handler nested deep inside the sidebar).
  // Updates both localStorage AND the current URL's `tabs` param in-place
  // so any open TabStrip re-renders without a full navigation.
  // Exposed below as `closeOpenTab` for external use.

  const reorder = useCallback((from: number, to: number) => {
    setTabs((prev) => {
      if (from < 0 || to < 0 || from >= prev.length || to >= prev.length) return prev;
      const next = prev.slice();
      const [item] = next.splice(from, 1);
      next.splice(to, 0, item);
      return next;
    });
  }, [setTabs]);

  return { tabs, close, closeAll, reorder, setTabs };
}

// Remove a request id from both the localStorage tab list AND the URL's
// `?tabs=…` param without going through the hook. Useful for the
// per-row delete handler which would otherwise leave a phantom tab.
//
// Returns the new search string (for navigate()) plus the next-active
// request id the caller should focus when the closed tab was the
// current one. Picks the tab that sat immediately to the right of the
// closed one — same convention browsers and editors use — falling back
// to the new rightmost tab if the closed tab was last, or null when
// nothing else is open.
export function closeOpenTabFor(workspaceId: string, requestId: string): { search: string; nextActive: string | null } {
  // localStorage half — drop the dead tab.
  const stored = readLS(workspaceId);
  if (stored.includes(requestId)) writeLS(workspaceId, stored.filter((t) => t !== requestId));
  // URL half — compute the new search string from window.location and
  // return it. We don't mutate window.location directly because react-
  // router's setSearchParams won't notice; instead the caller hands
  // this string to `navigate({ pathname, search })`.
  const sp = new URLSearchParams(window.location.search);
  const tabsBefore = parseParam(sp.get(PARAM));
  const closedAt = tabsBefore.indexOf(requestId);
  const tabs = tabsBefore.filter((t) => t !== requestId);
  if (tabs.length === 0) sp.delete(PARAM);
  else sp.set(PARAM, tabs.join(","));
  const s = sp.toString();
  // Pick the successor: tab to the right of the closed one, else the
  // new rightmost. `closedAt` is the pre-filter index; the post-filter
  // tab at that same index is the original next-neighbor (since the
  // dropped element was BEFORE everything to its right).
  let nextActive: string | null = null;
  if (tabs.length > 0) {
    if (closedAt >= 0 && closedAt < tabs.length) nextActive = tabs[closedAt];
    else nextActive = tabs[tabs.length - 1];
  }
  return { search: s ? `?${s}` : "", nextActive };
}
